import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import s from "./MyPayroll.module.css";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

/** Pick the employee code the current account is bound to. Older accounts
 *  set employeeCode directly; accounts created through the Phase-3 link
 *  flow only carry it inside linkedEntity.code. Either is fine. */
function resolveEmployeeCode(u) {
  if (!u) return "";
  if (u.employeeCode) return u.employeeCode;
  if (u.linkedEntity && u.linkedEntity.code) return u.linkedEntity.code;
  return "";
}

function authHeaders() {
  const u = getUser() || {};
  const code = resolveEmployeeCode(u);
  return {
    "Content-Type": "application/json",
    "X-User-Role":     u.role || "",
    "X-Employee-Code": code,
    "X-Username":      u.username || "system",
  };
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Parse the per-employee statutory-deductions JSON blob into a plain
 *  object. Shape mirrors Settings → Maintenance defaults: numeric SSS /
 *  PhilHealth / Pag-IBIG / Insurance plus a free-form "others" array
 *  (used for loans, advances, etc.). Returns zeroes on a malformed blob
 *  so the UI never crashes on legacy / hand-edited rows. */
function parseDeductions(raw) {
  const out = { sss: 0, philhealth: 0, pagibig: 0, insurance: 0, others: [] };
  if (!raw) return out;
  try {
    const o = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (o && typeof o === "object") {
      out.sss        = Number(o.sss)        || 0;
      out.philhealth = Number(o.philhealth) || 0;
      out.pagibig    = Number(o.pagibig)    || 0;
      out.insurance  = Number(o.insurance)  || 0;
      if (Array.isArray(o.others)) {
        out.others = o.others
          .map(x => ({ name: String(x?.name || ""), value: Number(x?.value) || 0 }))
          .filter(x => x.name);
      }
    }
  } catch { /* fall through to zeroes */ }
  return out;
}

/** Parse the per-employee earnings/allowances JSON blob (Transport,
 *  Meal, etc.). Total only — the breakdown surface is intentionally
 *  one row to keep the panel compact; the per-line breakdown lives on
 *  the admin Salary page. */
function parseEarnings(raw) {
  if (!raw) return [];
  try {
    const a = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(a)) return [];
    return a
      .map(x => ({ name: String(x?.name || ""), value: Number(x?.value) || 0 }))
      .filter(x => x.name);
  } catch { return []; }
}

/** Count Mon–Fri days in (year, month). Used as the divisor for the
 *  daily-rate calculation and for the absent-day count. Matches what
 *  the admin Salary page does so the two views agree on totals. */
function expectedWorkdays(year, month) {
  const days = new Date(year, month + 1, 0).getDate();
  let n = 0;
  for (let d = 1; d <= days; d++) {
    const dow = new Date(year, month, d).getDay();
    if (dow !== 0 && dow !== 6) n += 1;
  }
  return n;
}

/** Smoothly ramp a number from 0 → target (rAF). */
function useCountUp(target, durationMs = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const k = Math.min(1, (now - t0) / durationMs);
      // ease-out cubic for a tactile finish
      const eased = 1 - Math.pow(1 - k, 3);
      setValue(target * eased);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
}

/** Show / hide toggle for sensitive money values. The default state is
 *  HIDDEN — payroll is a privacy-by-default surface — so the icon shown
 *  on first paint is the "eye-off" (closed) glyph; clicking reveals.
 *  We keep both SVG paths in one button and just swap the active one,
 *  so the click-target stays in the same place across toggles. */
function EyeButton({ visible, onToggle }) {
  return (
    <button type="button"
            className={`${s.eyeBtn} ${visible ? s.eyeBtnOn : ""}`}
            onClick={onToggle}
            aria-pressed={visible}
            aria-label={visible ? "Hide amounts" : "Show amounts"}
            title={visible ? "Hide amounts" : "Show amounts"}>
      <span className={s.eyeIconWrap} aria-hidden="true">
        {visible ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        )}
      </span>
      <span className={s.eyeLabel}>{visible ? "Hide" : "Show"}</span>
    </button>
  );
}

/** One row inside the Payroll Computation card. Negative rows render
 *  with a leading "−" and a soft red value; zero rows render dimmed
 *  so the panel reads as "this line wasn't applicable" instead of
 *  competing for attention with the rows that actually moved money.
 *  When `hidden` is true the value cell renders the dot mask instead
 *  of the formatted amount — used by the privacy-toggle. */
function PayLine({ label, meta, value, positive, negative, hidden }) {
  const v = Number(value) || 0;
  const isZero = Math.abs(v) < 0.005;
  return (
    <div className={`${s.payLine} ${isZero && !hidden ? s.payLineZero : ""}`}>
      <div className={s.payLineLabel}>
        <span className={s.payLineName}>{label}</span>
        {meta && <span className={s.payLineMeta}>{meta}</span>}
      </div>
      <div className={`${s.payLineValue} ${negative ? s.negative : ""} ${positive ? s.positive : ""} ${hidden ? s.payLineHidden : ""}`}>
        {hidden
          ? "••••••"
          : `${negative && !isZero ? "− " : ""}₱${fmtMoney(v)}`}
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix, accent, icon, money }) {
  const display = useCountUp(Number(value) || 0);
  const text = money
    ? fmtMoney(display)
    : (Number.isInteger(value) ? Math.round(display).toString()
                               : display.toFixed(1));
  return (
    <div className={s.statCard}>
      <div className={`${s.statIcon} ${s[`accent${accent}`] || ""}`}>{icon}</div>
      <div className={s.statBody}>
        <div className={s.statLabel}>{label}</div>
        <div className={s.statValue}>
          {money && <span className={s.currency}>₱</span>}
          {text}
          {suffix && <span className={s.statSuffix}>{suffix}</span>}
        </div>
      </div>
    </div>
  );
}

export default function MyPayrollPage() {
  const user = getUser() || {};
  const employeeCode = resolveEmployeeCode(user);

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());     // 0-based
  const [year,  setYear]  = useState(today.getFullYear());

  const [employee, setEmployee]     = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [events, setEvents]         = useState([]); // calendar events for the selected month
  const [history, setHistory]       = useState([]); // [{label, hours}] last 6 months
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  // ── Privacy toggle ─────────────────────────────────────────────
  // Hide all money values by default — payroll is the kind of thing
  // people don't want over a colleague's shoulder. The user can reveal
  // by clicking the eye icon, and the choice persists in sessionStorage
  // so a re-navigation back to the page during the same session keeps
  // their preference. Logging out (which clears sessionStorage) resets
  // back to the safe default. localStorage was deliberately avoided
  // here so a shared workstation doesn't leak the choice across users.
  const [amountsVisible, setAmountsVisible] = useState(() => {
    try { return sessionStorage.getItem("mp.amountsVisible") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("mp.amountsVisible", amountsVisible ? "1" : "0"); }
    catch { /* private mode etc. — fall back to in-memory only */ }
  }, [amountsVisible]);

  /** Render either the formatted amount or a fixed-width dot mask.
   *  Using a literal string (not CSS blur) means the actual value is
   *  not in the DOM when hidden — DevTools / screenshots can't read it. */
  function mask(v) {
    return amountsVisible ? fmtMoney(v) : "••••••";
  }

  // ── Load employee record ───────────────────────────────────────
  useEffect(() => {
    if (!employeeCode) { setLoading(false); return; }
    let cancelled = false;
    fetch(`${API_BASE}/api/employees/${employeeCode}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error("Failed to load employee")))
      .then(emp => { if (!cancelled) setEmployee(emp); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeCode]);

  // ── Load current period timesheets ─────────────────────────────
  useEffect(() => {
    if (!employeeCode) return;
    const url = `${API_BASE}/api/timesheets?employeeCode=${employeeCode}&year=${year}&month=${month + 1}`;
    fetch(url, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(rows => setTimesheets(Array.isArray(rows) ? rows : []))
      .catch(() => setTimesheets([]));
  }, [employeeCode, year, month]);

  // ── Load calendar events for the selected month ────────────────
  // Drives the Special-Holiday / Regular-Holiday rows in the
  // computation panel. We pull only the visible month so the network
  // cost stays tiny and the "Holiday" detection is scoped to the
  // period being viewed.
  useEffect(() => {
    if (!employeeCode) return;
    let cancelled = false;
    const url = `${API_BASE}/api/calendar/events?year=${year}&month=${month + 1}&employeeCode=${employeeCode}`;
    fetch(url, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(rows => { if (!cancelled) setEvents(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setEvents([]); });
    return () => { cancelled = true; };
  }, [employeeCode, year, month]);

  // ── Load 6-month history for the trend strip ───────────────────
  useEffect(() => {
    if (!employeeCode) return;
    let cancelled = false;
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(year, month - i, 1);
      months.push({ y: d.getFullYear(), m: d.getMonth() });
    }
    Promise.all(
      months.map(({ y, m }) =>
        fetch(`${API_BASE}/api/timesheets?employeeCode=${employeeCode}&year=${y}&month=${m + 1}`,
              { headers: authHeaders() })
          .then(r => r.ok ? r.json() : [])
          .then(rows => {
            const total = (Array.isArray(rows) ? rows : [])
              .reduce((acc, t) => acc + (t.workedHours || 0), 0);
            return { label: MONTH_SHORT[m], hours: total };
          })
          .catch(() => ({ label: MONTH_SHORT[m], hours: 0 }))
      )
    ).then(rows => { if (!cancelled) setHistory(rows); });
    return () => { cancelled = true; };
  }, [employeeCode, year, month]);

  // ── Period totals from timesheets ──────────────────────────────
  const totals = useMemo(() => {
    let worked = 0, ot = 0, otApproved = 0, late = 0, undertime = 0;
    let daysWorked = 0, daysLeave = 0;
    for (const t of timesheets) {
      const w = t.workedHours || 0;
      worked += w;
      if (w > 0) daysWorked += 1;
      if ((t.overtimeHours || 0) > 0) {
        ot += t.overtimeHours;
        if ((t.overtimeStatus || "").toUpperCase() === "APPROVED") {
          otApproved += t.overtimeHours;
        }
      }
      late      += t.lateMinutes || 0;
      undertime += t.undertimeMinutes || 0;
      if (t.status === "Leave") daysLeave += 1;
    }
    return {
      worked: Math.round(worked * 10) / 10,
      ot,
      otApproved: Math.round(otApproved * 10) / 10,
      late,
      undertime,
      daysWorked,
      daysLeave,
    };
  }, [timesheets]);

  // ── Pay snapshot — full computation ────────────────────────────
  // Read-only personal preview matching the admin Salary page's logic
  // so the two views agree. The official payslip is still issued by HR;
  // this is a transparent breakdown of how the number is built up.
  //
  // Earnings:
  //   · Regular Pay      — full monthly salary (deductions handled below)
  //   · Allowances       — sum of employee.earningsJson rows
  //   · Overtime (+25%)  — approved OT hours × hourly × 1.25
  //   · Special Holiday  — extra +30% for each Special-Holiday day worked
  //   · Regular Holiday  — extra +100% for each Regular-Holiday day worked
  //
  // Deductions:
  //   · Late / Undertime — minutes × hourly rate
  //   · Absent           — daysAbsent × daily rate
  //   · SSS / PhilHealth / Pag-IBIG / Insurance / Others — from
  //     employee.deductionsJson; HR-managed and read-only here.
  //
  // Net Pay = (Regular + Allowances + OT + Holiday) − all deductions.
  const pay = useMemo(() => {
    const base = Number(employee?.salary) || 0;
    const expDays = expectedWorkdays(year, month);
    const dailyRate = expDays > 0 ? base / expDays : 0;
    const hourlyRate = dailyRate / 8;

    // Holidays in the visible month from the calendar feed.
    const specialDates = new Set();
    const regularDates = new Set();
    const allHolidayDates = new Set();
    for (const ev of events) {
      const t = (ev.eventType || "").toLowerCase();
      if (!ev.startDate) continue;
      const a = new Date(ev.startDate + "T00:00:00");
      const b = new Date((ev.endDate || ev.startDate) + "T00:00:00");
      if (isNaN(a) || isNaN(b)) continue;
      const cur = new Date(a);
      while (cur <= b) {
        const k = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`;
        if (t === "special holiday") { specialDates.add(k); allHolidayDates.add(k); }
        else if (t === "regular holiday") { regularDates.add(k); allHolidayDates.add(k); }
        else if (t === "holiday")        { allHolidayDates.add(k); }
        cur.setDate(cur.getDate() + 1);
      }
    }

    // Did the user actually clock in on a holiday? Only worked-holiday
    // days earn the bonus; rest-day holidays already sit in base pay.
    let specialWorked = 0, regularWorked = 0;
    for (const r of timesheets) {
      const k = String(r.date || "");
      if ((r.workedHours || 0) <= 0) continue;
      if (specialDates.has(k)) specialWorked += 1;
      else if (regularDates.has(k)) regularWorked += 1;
    }

    // Earnings rows
    const allowanceList = parseEarnings(employee?.earningsJson);
    const allowances    = allowanceList.reduce((a, x) => a + x.value, 0);
    const otPay         = totals.otApproved * hourlyRate * 1.25;
    const specialPay    = specialWorked * dailyRate * 0.30;
    const regularPay    = regularWorked * dailyRate * 1.00;

    // Deduction rows
    const ded = parseDeductions(employee?.deductionsJson);
    const otherDeductions = ded.others.reduce((a, x) => a + x.value, 0);
    const lateDeduction      = (totals.late      / 60) * hourlyRate;
    const undertimeDeduction = (totals.undertime / 60) * hourlyRate;

    // Days Absent = expected workdays − worked − leave − holiday days.
    // Clamp to 0 so a missing-data month doesn't fabricate a deduction.
    const daysAbsent = Math.max(
      0,
      expDays - totals.daysWorked - totals.daysLeave - allHolidayDates.size
    );
    const absentDeduction = daysAbsent * dailyRate;

    const earningsTotal =
      base + allowances + otPay + specialPay + regularPay;
    const deductionsTotal =
      lateDeduction + undertimeDeduction + absentDeduction +
      ded.sss + ded.philhealth + ded.pagibig + ded.insurance + otherDeductions;
    const gross = earningsTotal;
    const net   = Math.max(0, earningsTotal - deductionsTotal);

    return {
      base,
      dailyRate,
      hourlyRate,
      expDays,
      // Earnings
      allowances,
      allowanceList,
      otPay,
      specialPay,
      regularPay,
      // Deductions
      lateDeduction,
      undertimeDeduction,
      absentDeduction,
      daysAbsent,
      sss: ded.sss,
      philhealth: ded.philhealth,
      pagibig: ded.pagibig,
      insurance: ded.insurance,
      otherDeductions,
      otherDeductionsList: ded.others,
      // Totals
      gross,
      deductions: deductionsTotal,
      net,
    };
  }, [employee, events, year, month, totals]);

  // Animated headline numbers for the hero card. Both hooks must run
  // unconditionally to keep the hook order stable across the employee-
  // code / loading early returns below (react-hooks/rules-of-hooks).
  const animatedNet   = useCountUp(pay.net);
  const animatedGross = useCountUp(pay.gross);

  // 6-month trend bars — scale to max(40, peak) so an empty stretch
  // doesn't look like a flat line.
  const peakHours = Math.max(40, ...history.map(h => h.hours || 0));

  // ── Render ─────────────────────────────────────────────────────
  if (!employeeCode) {
    return (
      <div className={s.page}>
        <div className={s.emptyState}>
          <div className={s.emptyEmoji}>🔒</div>
          <h2>No payroll on file</h2>
          <p>Your account isn't linked to an employee record yet. Please contact HR.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      {/* ── Page header ── */}
      <header className={s.pageHeader}>
        <div className={s.headerLeft}>
          <div className={s.eyebrow}>My Payroll</div>
          <h1 className={s.pageTitle}>Pay summary</h1>
          <p className={s.pageSub}>
            Your hours, overtime, and pay snapshot for the selected period.
          </p>
        </div>

        <div className={s.headerRight}>
          {/* Privacy toggle — defaults to HIDDEN so a freshly-loaded page
              never shouts the user's salary at anyone walking past. */}
          <EyeButton visible={amountsVisible}
                     onToggle={() => setAmountsVisible(v => !v)} />

          <div className={s.periodPicker}>
            <button
              type="button"
              className={s.periodNav}
              onClick={() => {
                if (month === 0) { setMonth(11); setYear(y => y - 1); }
                else setMonth(m => m - 1);
              }}
              aria-label="Previous month">‹</button>
            <div className={s.periodLabel}>
              <span className={s.periodMonth}>{MONTH_NAMES[month]}</span>
              <span className={s.periodYear}>{year}</span>
            </div>
            <button
              type="button"
              className={s.periodNav}
              onClick={() => {
                if (month === 11) { setMonth(0); setYear(y => y + 1); }
                else setMonth(m => m + 1);
              }}
              aria-label="Next month">›</button>
          </div>
        </div>
      </header>

      {error && <div className={s.error}>{error}</div>}
      {loading && <div className={s.loading}>Loading your payroll…</div>}

      {!loading && employee && (
        <>
          {/* ── Hero employee + earnings card ── */}
          <section className={s.hero}>
            <div className={s.heroGlow} aria-hidden="true" />
            <div className={s.heroBody}>
              <div className={s.heroIdentity}>
                {employee.photoUrl ? (
                  <img src={employee.photoUrl} alt={employee.name}
                       className={s.heroAvatar} />
                ) : (
                  <div className={s.heroAvatarFallback}>
                    {(employee.name || user.username || "?")[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <div className={s.heroLabel}>Employee</div>
                  <div className={s.heroName}>{employee.name || user.username}</div>
                  <div className={s.heroMeta}>
                    {employee.position || user.role}
                    {employee.department && ` · ${employee.department}`}
                  </div>
                  <div className={s.heroBadge}>
                    <span className={s.heroBadgeDot} />
                    {employee.code}
                  </div>
                </div>
              </div>

              <div className={s.heroDivider} aria-hidden="true" />

              <div className={s.heroEarnings}>
                <div className={s.heroEarningsLabel}>Estimated net pay — {MONTH_NAMES[month]}</div>
                <div className={`${s.heroEarningsAmount} ${!amountsVisible ? s.heroEarningsHidden : ""}`}>
                  {amountsVisible && <span className={s.heroCurrency}>₱</span>}
                  <span className={s.heroBig}>
                    {amountsVisible ? fmtMoney(animatedNet) : "••••••"}
                  </span>
                </div>
                <div className={s.heroChips}>
                  <div className={`${s.chip} ${s.chipPos}`}>
                    <span className={s.chipLabel}>Earnings</span>
                    <span className={s.chipValue}>
                      {amountsVisible ? `₱${fmtMoney(animatedGross)}` : "₱••••••"}
                    </span>
                  </div>
                  <div className={`${s.chip} ${s.chipNeg}`}>
                    <span className={s.chipLabel}>− Deductions</span>
                    <span className={s.chipValue}>
                      {amountsVisible ? `₱${fmtMoney(pay.deductions)}` : "₱••••••"}
                    </span>
                  </div>
                  <div className={`${s.chip} ${s.chipBase}`}>
                    <span className={s.chipLabel}>Base</span>
                    <span className={s.chipValue}>
                      {amountsVisible ? `₱${fmtMoney(pay.base)}` : "₱••••••"}
                    </span>
                  </div>
                </div>
                <div className={s.heroNote}>
                  Snapshot only — your final payslip is issued by HR.
                </div>
              </div>
            </div>
          </section>

          {/* ── Stat tiles ── */}
          <section className={s.statsGrid}>
            <StatCard label="Hours worked" value={totals.worked} suffix=" hrs" accent="A"
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.9"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              } />
            <StatCard label="Overtime (approved)" value={totals.otApproved} suffix=" hrs" accent="B"
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.9"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
                </svg>
              } />
            <StatCard label="Days present" value={totals.daysWorked} accent="C"
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.1"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m4.5 12.5 5 5 10-11" />
                </svg>
              } />
            <StatCard label="Days on leave" value={totals.daysLeave} accent="D"
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.8"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2.5" />
                  <path d="M16 3v4M8 3v4M3 10h18" />
                </svg>
              } />
          </section>

          {/* ═══════════════ Payroll Computation ═══════════════
              Two-column transparent breakdown: every component that
              feeds into Gross / Deductions / Net is named and sourced
              so the user can audit their own number. The official
              payslip is still HR-issued — this is a snapshot. */}
          <section className={`${s.card} ${s.computeCard}`}>
            <div className={s.cardHead}>
              <h3 className={s.cardTitle}>Payroll computation</h3>
              <div className={s.computeMeta}>
                <span><strong>Daily</strong> ₱{mask(pay.dailyRate)}</span>
                <span className={s.metaDot} aria-hidden="true">·</span>
                <span><strong>Hourly</strong> ₱{mask(pay.hourlyRate)}</span>
                <span className={s.metaDot} aria-hidden="true">·</span>
                <span>{totals.daysWorked} of {pay.expDays} days worked</span>
              </div>
            </div>

            <div className={s.computeGrid}>
              {/* ── Earnings column ── */}
              <div className={s.computeCol}>
                <div className={s.computeColHead}>
                  <span className={`${s.colDot} ${s.colDotPos}`} />
                  Earnings
                </div>

                <PayLine label="Regular Pay" value={pay.base} positive hidden={!amountsVisible} />
                {pay.allowanceList.length > 0 && (
                  <PayLine label="Allowances"
                           meta={pay.allowanceList.map(x => x.name).join(", ")}
                           value={pay.allowances} positive hidden={!amountsVisible} />
                )}
                <PayLine label="Overtime" meta={`+25% · ${totals.otApproved.toFixed(1)}h`}
                         value={pay.otPay} positive hidden={!amountsVisible} />
                <PayLine label="Special Holiday" meta="+30%"
                         value={pay.specialPay} positive hidden={!amountsVisible} />
                <PayLine label="Regular Holiday" meta="+100%"
                         value={pay.regularPay} positive hidden={!amountsVisible} />

                <div className={s.computeSubtotal}>
                  <span>Total earnings</span>
                  <strong>₱{mask(pay.gross)}</strong>
                </div>
              </div>

              {/* ── Deductions column ── */}
              <div className={s.computeCol}>
                <div className={s.computeColHead}>
                  <span className={`${s.colDot} ${s.colDotNeg}`} />
                  Deductions
                </div>

                <PayLine label="Late" meta={`${totals.late} min`}
                         value={pay.lateDeduction} negative hidden={!amountsVisible} />
                <PayLine label="Undertime" meta={`${totals.undertime} min`}
                         value={pay.undertimeDeduction} negative hidden={!amountsVisible} />
                <PayLine label="Absent"
                         meta={`${pay.daysAbsent} day${pay.daysAbsent === 1 ? "" : "s"}`}
                         value={pay.absentDeduction} negative hidden={!amountsVisible} />
                <PayLine label="SSS"        value={pay.sss}        negative hidden={!amountsVisible} />
                <PayLine label="PhilHealth" value={pay.philhealth} negative hidden={!amountsVisible} />
                <PayLine label="Pag-IBIG"   value={pay.pagibig}    negative hidden={!amountsVisible} />
                <PayLine label="Insurance"  value={pay.insurance}  negative hidden={!amountsVisible} />
                {pay.otherDeductionsList.length > 0 && (
                  <PayLine label="Other"
                           meta={pay.otherDeductionsList.map(x => x.name).join(", ")}
                           value={pay.otherDeductions} negative hidden={!amountsVisible} />
                )}

                <div className={s.computeSubtotal}>
                  <span>Total deductions</span>
                  <strong>₱{mask(pay.deductions)}</strong>
                </div>
              </div>
            </div>

            {/* ── Final summary: Gross · Deductions · Net Pay ── */}
            <div className={s.netSummary}>
              <div className={s.netCol}>
                <div className={s.netColLabel}>Gross pay</div>
                <div className={s.netColValue}>₱{mask(pay.gross)}</div>
              </div>
              <div className={s.netSep} aria-hidden="true">−</div>
              <div className={s.netCol}>
                <div className={s.netColLabel}>Total deductions</div>
                <div className={`${s.netColValue} ${s.netNeg}`}>₱{mask(pay.deductions)}</div>
              </div>
              <div className={s.netSep} aria-hidden="true">=</div>
              <div className={`${s.netCol} ${s.netColPrimary}`}>
                <div className={s.netColLabel}>Net pay</div>
                <div className={s.netColValue}>₱{mask(pay.net)}</div>
              </div>
            </div>

            <div className={s.computeFootnote}>
              Snapshot only — final figures are issued by HR on your payslip.
              Statutory deductions (SSS, PhilHealth, Pag-IBIG) follow the values set on your employee profile.
            </div>
          </section>

          {/* ── 6-month worked-hours trend (full-width) ── */}
          <section className={`${s.card} ${s.trendCard}`}>
            <div className={s.cardHead}>
              <h3 className={s.cardTitle}>Hours over the last 6 months</h3>
              <span className={s.cardSub}>Worked hours</span>
            </div>
            <div className={s.trendBars}>
              {history.map((h, i) => {
                const pct = Math.max(2, (h.hours / peakHours) * 100);
                const isCurrent = i === history.length - 1;
                return (
                  <div key={i} className={s.trendCol}>
                    <div className={s.trendValue}>{h.hours.toFixed(0)}h</div>
                    <div className={s.trendBarOuter}>
                      <div
                        className={`${s.trendBar} ${isCurrent ? s.trendBarCurrent : ""}`}
                        style={{ height: `${pct}%`, animationDelay: `${i * 80}ms` }}
                      />
                    </div>
                    <div className={`${s.trendLabel} ${isCurrent ? s.trendLabelCurrent : ""}`}>
                      {h.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Recent timesheets ── */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h3 className={s.cardTitle}>Recent entries</h3>
              <span className={s.cardSub}>{MONTH_NAMES[month]} {year}</span>
            </div>

            {timesheets.length === 0 ? (
              <div className={s.tableEmpty}>
                No timesheet entries recorded for this period.
              </div>
            ) : (
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time in</th>
                      <th>Time out</th>
                      <th className={s.right}>Hours</th>
                      <th className={s.right}>Overtime</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timesheets
                      .slice()
                      .sort((a, b) => String(b.date).localeCompare(String(a.date)))
                      .slice(0, 10)
                      .map((t, i) => {
                        const ot = t.overtimeHours || 0;
                        const otStatus = (t.overtimeStatus || "").toUpperCase();
                        return (
                          <tr key={t.id || i} style={{ animationDelay: `${i * 40}ms` }}>
                            <td>{t.date}</td>
                            <td>{t.timeIn || "—"}</td>
                            <td>{t.timeOut || "—"}</td>
                            <td className={s.right}>{(t.workedHours || 0).toFixed(1)}</td>
                            <td className={s.right}>
                              {ot > 0 ? (
                                <span className={
                                  otStatus === "APPROVED" ? s.pillOk :
                                  otStatus === "REJECTED" ? s.pillBad :
                                  s.pillWait
                                }>
                                  {ot.toFixed(1)}h {otStatus || "PENDING"}
                                </span>
                              ) : "—"}
                            </td>
                            <td>
                              <span className={
                                t.status === "Leave"   ? s.statusLeave :
                                t.status === "Holiday" ? s.statusHoliday :
                                s.statusOk
                              }>
                                {t.status || "Worked"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
